import { useCallback } from "react";
import type { DataEnrichmentUnmatchedSuggestionEntryDto } from "@advanced-sales-technologies/backend-client/models/DataEnrichmentUnmatchedSuggestionEntryDto";
import { UseFormSetValue, UseFormUnregister } from "react-hook-form/dist/types/form";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowLongRight, HiOutlineTrash } from "react-icons/hi2";

import { useDiscardUnmatched } from "@/hooks/api/list/enrich/useDiscardUnmatched";
import { useCompanyAutocomplete } from "@/hooks/api/search/useCompanyAutocomplete";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import Button from "@/components/atoms/Button";
import Chip from "@/components/atoms/Chip";
import FormComboBox from "@/components/molecules/form/FormComboBox";
import { UnmatchedFormValues } from "@/components/molecules/modals/matching/MapUnmatchedItemsModal";
import { CompanyFilterOption } from "@/components/organisms/filter/CompanyFilter";

type SelectProps = {
  listId: string;
  unmatchedItem: DataEnrichmentUnmatchedSuggestionEntryDto;
  search?: string;
  setValue: UseFormSetValue<UnmatchedFormValues>;
  unregister: UseFormUnregister<UnmatchedFormValues>;
};

const MatchUnmatchedRowItem = ({ listId, unmatchedItem, search, setValue, unregister }: SelectProps) => {
  const { t } = useTranslation();

  const { query, setDebounceQuery, isTyping } = useDebounceQuery();
  const { data: options, isLoading } = useCompanyAutocomplete(query);

  const { discardUnmatched, isPending: discardUnmatchedIsLoading } = useDiscardUnmatched({ query: search || "" });

  const setSelectValue = useCallback(
    (value: { id: string; name: string }) => {
      setValue(`items.${unmatchedItem.id}`, value);
    },
    [setValue, unmatchedItem.id],
  );

  return (
    <div className="grid grid-cols-2 items-center gap-x-16 border-secondary px-4 py-3 text-sm font-medium [&:not(:first-child)]:border-t">
      <div className="flex justify-between self-start p-2">
        <div className={"grid space-y-0.5"}>
          {unmatchedItem.company_name && <p className="truncate">{unmatchedItem.company_name}</p>}
          {unmatchedItem.email && <p className="truncate">{unmatchedItem.email}</p>}
          {unmatchedItem.domain && <p className="truncate">{unmatchedItem.domain}</p>}
        </div>
        <HiOutlineArrowLongRight className="mt-1 size-5" />
      </div>

      <div>
        <div className={"flex gap-3"}>
          <div className={"flex-1"}>
            <FormComboBox
              allowFreeText={false}
              name={`items.${unmatchedItem.id}`}
              query={query}
              setQuery={setDebounceQuery}
              options={options || []}
              placeholder={t("unmatched.placeholder")}
              isLoading={isTyping || isLoading}
              renderOption={CompanyFilterOption}
              optionsSearchable={false}
            />
          </div>
          <Button
            type={"button"}
            onClick={() =>
              discardUnmatched(
                { listId: listId, entryId: unmatchedItem.id },
                { onSuccess: () => unregister(`items.${unmatchedItem.id}`) },
              )
            }
            label={t("unmatched.discard")}
            color={"error"}
            isLoading={discardUnmatchedIsLoading}
            variants={"text"}
            size={"xs"}
            leadingIcon={<HiOutlineTrash className={"size-5"} />}
          />
        </div>
        <div className={"mt-3 grid gap-3"}>
          <h4 className={"text-xs font-medium text-tertiary"}>{t("unmatched.suggested")} </h4>
          {unmatchedItem.suggestions.length > 0 ? (
            <div className={"flex flex-wrap gap-2"}>
              {unmatchedItem.suggestions
                .filter((suggestion) => suggestion.name !== undefined)
                .map((suggestion) => (
                  <Chip
                    className={"max-w-52"}
                    key={suggestion.company_id}
                    label={suggestion.name}
                    size={"xs"}
                    color={"natural"}
                    onClick={() => setSelectValue({ id: suggestion.company_id, name: suggestion.name! })}
                  />
                ))}
            </div>
          ) : (
            <p className={"text-xs text-tertiary"}>{t("unmatched.nothingFound.title")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchUnmatchedRowItem;
